import { userService } from '@/services/user.service';
import { useAuthStore } from '@/store/auth.store';
import { UpdatePreferencesData } from '@/types/auth.types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const useUpdatePreferences = () => {
  const { t, i18n } = useTranslation();
  const { setUser } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: UpdatePreferencesData) =>
      userService.updatePreferences(preferences),
    onSuccess: response => {
      // Update the user in the auth store
      setUser(response.data);

      // Update React Query cache
      queryClient.setQueryData(['profile'], response);

      // Update language if it changed
      if (response.data.preferredLanguage !== i18n.language) {
        i18n.changeLanguage(response.data.preferredLanguage);
      }

      toast.success(
        t(
          'settings.preferences.update.success',
          'Préférences mises à jour avec succès'
        )
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t(
          'settings.preferences.update.error',
          'Erreur lors de la mise à jour des préférences'
        );
      toast.error(message);
    },
  });
};

export const useToggleNotification = (type: 'email' | 'push') => {
  const { user } = useAuthStore();
  const updatePreferences = useUpdatePreferences();

  return (enabled: boolean) => {
    if (!user) return;

    const preferences: UpdatePreferencesData = {
      emailNotifications: type === 'email' ? enabled : user.emailNotifications,
      pushNotifications: type === 'push' ? enabled : user.pushNotifications,
    };

    updatePreferences.mutate(preferences);
  };
};

export const useUpdateLanguage = () => {
  const updatePreferences = useUpdatePreferences();

  return (language: string) => {
    updatePreferences.mutate({ preferredLanguage: language });
  };
};

export const useUpdateTimezone = () => {
  const updatePreferences = useUpdatePreferences();

  return (timezone: string) => {
    updatePreferences.mutate({ timezone });
  };
};

export const useUpdateDateFormat = () => {
  const updatePreferences = useUpdatePreferences();

  return (dateFormat: string) => {
    updatePreferences.mutate({ dateFormat });
  };
};
